/**
 * Custom hook for handling API calls with loading states and error handling
 * Eliminates duplicate try-catch-finally patterns across the application
 */

import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface UseApiCallOptions<T> {
  onSuccess?: (data: T) => void;
  onError?: (error: string) => void;
  successMessage?: string;
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export interface UseApiCallReturn<T> {
  execute: (
    apiCall: () => Promise<ApiResponse<T>>,
    options?: UseApiCallOptions<T>
  ) => Promise<T | null>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for managing API calls with consistent error handling and loading states
 *
 * @example
 * const api = useApiCall<LessonRequest[]>();
 *
 * const loadData = async () => {
 *   const data = await api.execute(
 *     () => lessonAPI.getRequests({ page: 1 }),
 *     {
 *       successMessage: '데이터를 불러왔습니다',
 *       errorMessage: '데이터 로드 중 오류가 발생했습니다',
 *       onSuccess: (data) => setRequests(data)
 *     }
 *   );
 * };
 */
export const useApiCall = <T = any>(): UseApiCallReturn<T> => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (
      apiCall: () => Promise<ApiResponse<T>>,
      options: UseApiCallOptions<T> = {}
    ): Promise<T | null> => {
      const {
        onSuccess,
        onError,
        successMessage,
        errorMessage = '오류가 발생했습니다',
        showSuccessToast = !!successMessage,
        showErrorToast = true,
      } = options;

      try {
        setLoading(true);
        setError(null);

        const response = await apiCall();

        if (response.success && response.data !== undefined) {
          // Success handling
          if (showSuccessToast && successMessage) {
            toast.success(successMessage);
          }

          if (onSuccess) {
            onSuccess(response.data);
          }

          return response.data;
        } else {
          // API returned unsuccessful response
          const errorMsg = response.message || response.error || errorMessage;

          if (showErrorToast) {
            toast.error(errorMsg);
          }

          if (onError) {
            onError(errorMsg);
          }

          setError(errorMsg);
          return null;
        }
      } catch (err) {
        // Exception handling
        const errorMsg = err instanceof Error
          ? err.message
          : errorMessage;

        if (showErrorToast) {
          toast.error(errorMsg);
        }

        if (onError) {
          onError(errorMsg);
        }

        setError(errorMsg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  return {
    execute,
    loading,
    error,
    reset,
  };
};
